/* @flow */
import React, { type Node, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import Breadcrumbs from '../../components/Breadcrumbs';
import Sorting from '../../components/Sorting';
import { Header, Root, Title } from '../knowledge/styles';
import { Container } from '../../components/Layout/styles';
import { DeveloperCardRoot, PaginationWrapper, Wrapper } from './styles';
import theme from '../../theme';
import DeveloperCard from '../../components/DeveloperCard';
import Paginate from '../../components/Paginate';
import type { TDosier, TDosierItem } from '../../types/dosier';
import { changeDosiersPage, getDosiersData, sortingDirectionDosiers, sortingTypeDosiers } from '../../actions/dosier';
import type { TDispatch } from '../../types/core';
import { AppContext } from '../../components/AppContext';
import type { TRootState } from '../../types/rootState';
import history from '../../history';
import type { TCity } from '../../types/city';

const sorting = [
	{
		title: 'По количеству строящихся объектов',
		type: 'building',
	},
	{
		title: 'Дольше всех на рынке',
		type: 'ground_date',
	},
];

const Dosier = (): Node => {
	const dispatch: TDispatch = useDispatch();
	const store = useStore();
	const context = useContext(AppContext);

	const dosier: TDosier = useSelector((state: TRootState) => state.dosier);
	const city: TCity = useSelector((state: TRootState) => state.city);
	const isMobile: boolean = useSelector((state: TRootState) => state.app.isMobile);
	const link = `/${city.link}/zastroischiki/`;

	const {
		dosiersData: {
			items: dosiersList,
			meta: { pageNumber, itemsOnPage, itemsTotal },
		},
		sortType,
		sortDirection,
		isLoaded,
		isLoading,
		needUpdate,
	} = dosier;

	const cardsRef = useRef<Array<HTMLDivElement | null>>([]);

	useEffect(() => {
		if (dosiersList?.length === 0 && !isLoaded && !isLoading) {
			getDosiersData({ page: pageNumber })(dispatch, store.getState, context);
		}
	}, []);

	useEffect(() => {
		if (needUpdate) {
			getDosiersData({ page: pageNumber })(dispatch, store.getState, context);
		}
	}, [needUpdate]);

	useEffect(() => {
		if (cardsRef?.current[0]) {
			cardsRef.current[0].scrollIntoView();
			cardsRef.current[0].focus();
		}
	}, [pageNumber]);

	const onPageChange = (page) => async () => {
		history.replace({
			search: `?page=${page}${sortType ? `&sort=${sortDirection === 'down' ? '-' : ''}${sortType}` : ''}`,
		});
		getDosiersData({ page: page - 1 })(dispatch, store.getState, context);
	};

	const handleSortChange = (type) => {
		history.replace({
			// eslint-disable-next-line no-nested-ternary
			search: `?page=1&sort=${type === sortType ? `${sortDirection === 'up' ? '-' : ''}${type}` : type}`,
		});
		dispatch(changeDosiersPage(0));
		dispatch(sortingTypeDosiers(type));
		dispatch(sortingDirectionDosiers(type === sortType ? (sortDirection === 'up' ? 'down' : 'up') : 'up'));
	};

	const breadcrumbs = [
		{
			index: 1,
			title: 'Главная',
			link: '/',
		},
		{
			index: 2,
			title: 'Застройщики',
			link,
		},
	];

	return (
		<Root>
			<Container>
				<Breadcrumbs
					data={breadcrumbs}
					mobileTitle="Назад"
					color={isMobile && theme.darkGrayColor}
					style={isMobile ? theme.typography.textMediumM : theme.typography.textMediumS}
				/>
				<Header id="developers">
					<Title>Список застройщиков</Title>
				</Header>
				{dosiersList?.length > 0 && (
					<Wrapper>
						<Sorting
							active={sortType}
							sorting={sorting}
							direction={sortDirection}
							action={handleSortChange}
						/>
						<DeveloperCardRoot>
							{dosiersList.map((item: TDosierItem, i: number) => (
								<DeveloperCard
									key={Math.random()}
									item={item}
									ref={(el) => (cardsRef.current[i] = el)}
								/>
							))}
						</DeveloperCardRoot>
						{itemsTotal > itemsOnPage && (
							<PaginationWrapper>
								<Paginate
									currentPage={pageNumber + 1}
									setCurrentPage={(page) => onPageChange(page)}
									totalPosts={itemsTotal}
									postPerPage={itemsOnPage}
								/>
							</PaginationWrapper>
						)}
					</Wrapper>
				)}
			</Container>
		</Root>
	);
};

export default Dosier;
